/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useRef, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, Typography, Box, MenuItem, Select, InputLabel, FormControl, TextField, useTheme, Snackbar, Alert, SnackbarCloseReason, LinearProgress, alpha, IconButton, DialogTitle, Backdrop, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptIcon from '@mui/icons-material/Receipt';
import MoneyIcon from '@mui/icons-material/Money';
import PixIcon from '@mui/icons-material/Pix';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import BlockIcon from '@mui/icons-material/Block';
import BadgeIcon from '@mui/icons-material/Badge';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import { FaturamentoService } from '../../../shared/services/api/Financeiro/Faturamento';
import * as yup from 'yup';
import { AutoCompleteContaBancariaSimple } from '../../../shared/forms';
import { Environment } from '../../../shared/environment';
import { ConfiguracaoPdvService } from '../../../shared/services/api/cadastros/ConfiguracaoPdv';
import { ContaBancariaService } from '../../../shared/services/api/cadastros/ContaBancaria';
import { PaymentMethodSelector, IFormaPagamentoOpcao } from './PaymentMethodSelector';
import { DocumentoResumoBar } from './DocumentoResumoBar';
import { FormaProntaPanel } from './FormaProntaPanel';
import { KeyHintRow } from './KeyHint';
import { isEventoDeDigitacao } from './keyboardUtils';

export interface IPagamentoData {
  id: string;
  idconta: string;
  cpf: string;
  nome: string;
  indpag: number;
  tpag: string;
  tcnpj?: string;
  tband?: string;
  caut?: string;
  promocao: boolean;
}
export interface PaymentDialogProps {
  open: boolean;
  selectedId: string | null;
  selectedtpag?: number;
  onClose: (value?: string) => void;
  numerodocumento?: number | string;
  valortotal?: number;
  parceironome?: string;
  emissaodata?: string;
}

// Componente de Loading de Tela Cheia escuro
const FullScreenLoading: React.FC<{ message?: string; progress?: number }> = ({
  message = "Emitindo nota fiscal...",
  progress
}) => {
  return (
    <Backdrop
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 2,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        flexDirection: 'column',
        gap: 3
      }}
      open={true}
    >
      <CircularProgress
        size={60}
        sx={{
          color: 'white',
          '& .MuiCircularProgress-circle': {
            strokeLinecap: 'round',
          }
        }}
      />
      <Typography
        variant="h6"
        sx={{
          color: 'white',
          fontWeight: 500,
          textAlign: 'center'
        }}
      >
        {message}
      </Typography>
      {progress !== undefined && progress > 0 && (
        <Box sx={{ width: '300px', mt: 1 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'white'
              }
            }}
          />
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5, display: 'block', textAlign: 'center' }}>
            {Math.round(progress)}%
          </Typography>
        </Box>
      )}
      <Typography
        variant="body2"
        sx={{
          color: 'rgba(255, 255, 255, 0.7)',
          textAlign: 'center'
        }}
      >
        Aguarde enquanto processamos sua solicitação...
      </Typography>
    </Backdrop>
  );
};

// Função para validar CPF
const validarCPF = (cpf: string): boolean => {
  cpf = cpf.replace(/[^\d]/g, '');
  if (cpf.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cpf)) return false;

  let soma = 0;
  for (let i = 0; i < 9; i++) {
    soma += parseInt(cpf.charAt(i)) * (10 - i);
  }
  let resto = 11 - (soma % 11);
  let digitoVerificador = resto === 10 || resto === 11 ? 0 : resto;
  if (digitoVerificador !== parseInt(cpf.charAt(9))) return false;

  soma = 0;
  for (let i = 0; i < 10; i++) {
    soma += parseInt(cpf.charAt(i)) * (11 - i);
  }
  resto = 11 - (soma % 11);
  digitoVerificador = resto === 10 || resto === 11 ? 0 : resto;
  if (digitoVerificador !== parseInt(cpf.charAt(10))) return false;

  return true;
};

const formatarCPF = (cpf: string): string => {
  cpf = cpf.replace(/[^\d]/g, '');
  if (cpf.length <= 3) return cpf;
  if (cpf.length <= 6) return cpf.replace(/(\d{3})(\d+)/, '$1.$2');
  if (cpf.length <= 9) return cpf.replace(/(\d{3})(\d{3})(\d+)/, '$1.$2.$3');
  return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d+)/, '$1.$2.$3-$4');
};

const formatDate = (dateString?: string) => {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);
  } catch {
    return dateString;
  }
};

// CPF/nome do consumidor na nota são opcionais (aba "CPF na nota" na
// lateral da dialog) — só validam formato SE o operador preencheu algo,
// nunca bloqueiam a emissão por estarem vazios.
const getValidationSchema = (isPagamentoCartao: boolean, isPagamentoPix: boolean) => {
  const schemaShape: any = {
    id: yup.string().required('O ID é obrigatório'),
    indpag: yup.number().required('Informe o tipo de pagamento'),
    idconta: yup.string().required('Cadastre uma conta Padrão bancária'),
    tpag: yup.string().required('Selecione a forma de pagamento'),
    promocao: yup.boolean().required(),
    tband: yup.string().optional(),
    caut: yup.string().optional(),
    tcnpj: yup.string().optional(),
    cpf: yup
      .string()
      .optional()
      .test('cpf-valid', 'CPF inválido', (value) => {
        if (!value) return true;
        const cpfLimpo = value.replace(/[^\d]/g, '');
        if (cpfLimpo.length === 14) return false;
        return validarCPF(cpfLimpo);
      })
      .test('not-cnpj', 'CNPJ não é permitido, informe um CPF válido', (value) => {
        if (!value) return true;
        const cpfLimpo = value.replace(/[^\d]/g, '');
        if (cpfLimpo.length === 14) return false;
        return cpfLimpo.length === 11;
      }),
    nome: yup
      .string()
      .optional()
      .test('nome-valid', 'Nome deve ter entre 3 e 100 caracteres', (value) => {
        if (!value) return true;
        return value.length >= 3 && value.length <= 100;
      }),
  };

  if (isPagamentoCartao) {
    schemaShape.tband = yup.string().required('Bandeira do cartão é obrigatória');
    schemaShape.caut = yup.string().required('Código de autorização é obrigatório');
  }

  if (isPagamentoPix) {
    schemaShape.caut = yup.string().required('ID da transação PIX é obrigatório');
  }

  return yup.object().shape(schemaShape);
};

const TODAS_FORMAS_PAGAMENTO: IFormaPagamentoOpcao[] = [
  { id: '01', nome: 'Dinheiro', icon: <MoneyIcon fontSize="small" /> },
  { id: '17', nome: 'PIX Dinâmico', icon: <PixIcon fontSize="small" /> },
  { id: '20', nome: 'PIX Estático', icon: <PixIcon fontSize="small" /> },
  { id: '04', nome: 'Cartão de Débito', icon: <CreditCardIcon fontSize="small" /> },
  { id: '03', nome: 'Cartão de Crédito', icon: <CreditCardIcon fontSize="small" /> },
  { id: '18', nome: 'Transferência', icon: <SwapHorizIcon fontSize="small" /> },
  { id: '90', nome: 'Sem Pagamento', icon: <BlockIcon fontSize="small" /> },
];

const BANDEIRAS_CARTAO = [
  { id: '01', nome: 'Visa', cor: '#1a1f71' },
  { id: '02', nome: 'Mastercard', cor: '#eb001b' },
  { id: '06', nome: 'Elo', cor: '#00a4e0' },
  { id: '07', nome: 'Hipercard', cor: '#FFB90A' },
  { id: '03', nome: 'American Express', cor: '#287EFF' },
  { id: '99', nome: 'Outros', cor: '#666666' },
];

export const Dialogsendnfce: React.FC<PaymentDialogProps> = (props) => {
  const { onClose, open, selectedId, selectedtpag, numerodocumento, valortotal, parceironome, emissaodata } = props;
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [showFullScreenLoading, setShowFullScreenLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Emitindo nota fiscal...');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [idcontaError, setIdcontaError] = useState('');
  // Botão "Verificar Nota" — mesma checagem de configuração tributária dos
  // itens (NCM/CFOP/origem/CST/CST PIS/CST COFINS) que já existe na NFe,
  // aqui rodada contra os itens da venda (ver VerificarNfce.ts no backend).
  const [verificandoNota, setVerificandoNota] = useState(false);

  // Estados para o visualizador de PDF
  const [openPdfViewer, setOpenPdfViewer] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);

  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const pagamentoInicial: IPagamentoData = {
    id: '',
    indpag: 0,
    tpag: '01',
    cpf: '',
    nome: '',
    tband: '',
    caut: '',
    promocao: false,
    tcnpj: '',
    idconta: Environment.URL_IDCONTA || '',
  };

  const [pagamento, setPagamento] = useState<IPagamentoData>(pagamentoInicial);
  // Forma "sob o cursor" ao navegar com seta — só vira de fato pagamento.tpag
  // quando o operador confirma com Enter (ver PaymentMethodSelector.tsx).
  // Sempre volta a acompanhar pagamento.tpag depois de qualquer confirmação
  // (seta+Enter, número ou clique), pra nunca ficar "preso" destacando uma
  // opção diferente da que já está de fato selecionada.
  const [formaDestacada, setFormaDestacada] = useState(pagamentoInicial.tpag);
  useEffect(() => {
    setFormaDestacada(pagamento.tpag);
  }, [pagamento.tpag]);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('info');
  const [cpfError, setCpfError] = useState('');
  const [nomeError, setNomeError] = useState('');
  const [tbandError, setTbandError] = useState('');
  const [cautError, setCautError] = useState('');
  // CPF/nome do consumidor na nota são opcionais e ficam numa dialog à
  // parte, aberta pelo atalho "CPF na nota" no resumo (no lugar do nome do
  // cliente — NFC-e não tem cliente cadastrado na venda) ou apertando F6.
  const [openCpfDialog, setOpenCpfDialog] = useState(false);

  // Refs pro foco automático (F4/F6/Enter avançando entre campos — ver
  // handleDialogKeyDown/focarProximoCampoObrigatorio abaixo).
  const contaInputRef = useRef<HTMLInputElement>(null);
  const bandeiraSelectRef = useRef<HTMLInputElement>(null);
  const cautInputRef = useRef<HTMLInputElement>(null);
  const cpfInputRef = useRef<HTMLInputElement>(null);
  const emitirButtonRef = useRef<HTMLButtonElement>(null);
  const paymentSelectorRef = useRef<HTMLDivElement>(null);
  // Confirmar uma forma por CLIQUE deve manter o foco na listagem (o
  // operador espera continuar navegando com ↑↓ logo em seguida) — só o
  // Enter (teclado) e os atalhos numéricos avançam pro próximo campo
  // obrigatório sozinhos. Ref (não state) porque só precisa ser lido/escrito
  // de dentro de callbacks e efeitos, sem precisar re-renderizar nada.
  const avancarFocoAutomaticoRef = useRef(true);
  // Conta bancária padrão pra PIX/Transferência, configurada em Configurações
  // do PDV (idcontapadraopix) — buscada uma vez ao abrir a dialog e usada só
  // pra pré-preencher o autocomplete de conta ao selecionar essas formas (ver
  // selecionarForma). Ref, não state: não precisa re-renderizar nada sozinha,
  // só ser lida no momento da seleção.
  const contaPadraoPixRef = useRef('');

  // Simular progresso
  const simulateProgress = () => {
    setLoadingProgress(0);
    const interval = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 90) {
          clearInterval(interval);
          return 90;
        }
        return prev + 10;
      });
    }, 800);
    return () => clearInterval(interval);
  };
  const resetDialog = () => {
    setPagamento({
      ...pagamentoInicial,
      id: selectedId || '',
    });
    setMsg('');
    setTms('info');
    setOpenmsg(false);
    setCpfError('');
    setNomeError('');
    setTbandError('');
    setCautError('');
    setIdcontaError('');
    setOpenCpfDialog(false);
    avancarFocoAutomaticoRef.current = true;
    setShowFullScreenLoading(false);
    setLoadingProgress(0);
    setLoadingMessage('Emitindo nota fiscal...');
  };

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') return;
    setOpenmsg(false);
    setMsg('');
    setTms('info');
  };

  // Função para fechar o visualizador de PDF
  const handleClosePdfViewer = () => {
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
    setOpenPdfViewer(false);
    // Fecha o dialog principal após fechar o visualizador
    onClose();
  };

  // Função para download do PDF
  const handleDownloadPdf = () => {
    if (pdfBlobUrl) {
      const link = document.createElement('a');
      link.href = pdfBlobUrl;
      link.download = `nfce_${selectedId}.pdf`;
      link.click();
    }
  };

  // Função para imprimir
  const handlePrintPdf = () => {
    if (pdfBlobUrl) {
      const printWindow = window.open(pdfBlobUrl, '_blank');
      printWindow?.print();
    }
  };

  useEffect(() => {
    if (open) {
      resetDialog();
    }
  }, [open]);

  // Foca a lista de formas de pagamento assim que a dialog abre — sem isso,
  // as setas não navegam em lugar nenhum até o operador clicar na lista
  // (o container só reage a ↑↓ quando ele mesmo está com foco).
  useEffect(() => {
    if (!open) return;
    const timeoutId = setTimeout(() => paymentSelectorRef.current?.focus(), 100);
    return () => clearTimeout(timeoutId);
  }, [open]);

  // Conta padrão de PIX/Transferência configurada no PDV — buscada uma vez
  // ao abrir, pra já estar disponível quando o operador selecionar uma
  // dessas formas (ver selecionarForma/contaPadraoPixRef).
  useEffect(() => {
    if (!open) return;
    let cancelado = false;
    ConfiguracaoPdvService.get().then((resultado) => {
      if (cancelado || resultado instanceof Error) return;
      contaPadraoPixRef.current = resultado.idcontapadraopix || '';
    });
    return () => { cancelado = true; };
  }, [open]);

  useEffect(() => {
    if (selectedId) {
      setPagamento((prev) => ({ ...prev, id: selectedId }));
    }
  }, [selectedId, open]);

  // Cleanup ao desmontar
  useEffect(() => {
    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [pdfBlobUrl]);

  const isPagamentoCartao = pagamento.tpag === '03' || pagamento.tpag === '04';
  const isPagamentoPix = pagamento.tpag === '17';
  // PIX Estático e Transferência: sem rota fixa, o operador escolhe a conta
  // BANCÁRIA que recebe (filtrado por tipo='banco' no autocomplete).
  const isPagamentoContaDestino = pagamento.tpag === '20' || pagamento.tpag === '18';
  // Sem Pagamento: também sem rota fixa, mas aqui a conta pode ser QUALQUER
  // uma (caixa ou banco) — o operador decide onde aquele valor "existe",
  // já que não veio de uma forma de pagamento de verdade.
  const isPagamentoSemPagamento = pagamento.tpag === '90';
  const isPagamentoDinheiro = pagamento.tpag === '01';
  const shouldShowBandeira = isPagamentoCartao;
  const shouldShowCaut = isPagamentoCartao || isPagamentoPix;
  // Dinheiro nunca pede conta — é sempre o caixa da sessão ativa de quem
  // está emitindo (ver GeraNFCe.ts, roteamento por categoria), então o campo
  // ficaria só confundindo: qualquer conta escolhida aqui seria ignorada.
  // Conta só é escolhida manualmente pra PIX Estático/Transferência/Sem
  // Pagamento — Dinheiro, PIX Dinâmico e Cartão Crédito/Débito sempre
  // roteiam pra uma conta FIXA no backend (caixa da sessão ou recebível de
  // categoria — ver resolverContaRecebivelPorCategoria em GeraNFCe.ts), o
  // operador nunca escolhe conta pra essas formas.
  const shouldShowConta = isPagamentoContaDestino || isPagamentoSemPagamento;

  const selecionarForma = (id: string) => {
    const forma = TODAS_FORMAS_PAGAMENTO.find((f) => f.id === id);
    if (!forma) return;
    // Formas com conta escolhida manualmente (PIX Estático, Transferência,
    // Sem Pagamento) começam pré-preenchidas com a conta padrão de PIX
    // configurada no PDV (idcontapadraopix, ver contaPadraoPixRef acima),
    // quando existir — pré-preencher com a conta padrão (Environment.URL_IDCONTA,
    // que é o caixa) fazia esse caixa aparecer selecionado dentro de um
    // autocomplete que só deveria aceitar banco (PIX Estático/Transferência),
    // então usamos a conta do PDV em vez disso. A busca por "só existe uma
    // conta válida" (ver useEffect abaixo) só entra em ação se não houver
    // conta configurada.
    const exigeContaManual = forma.id === '20' || forma.id === '18' || forma.id === '90';
    setPagamento((prev) => ({
      ...prev,
      tpag: forma.id,
      tband: '',
      caut: '',
      idconta: exigeContaManual ? contaPadraoPixRef.current : (Environment.URL_IDCONTA || ''),
    }));
    setTbandError('');
    setCautError('');
    setIdcontaError('');
  };

  // Se só existe UMA conta válida (do tipo certo) pra essa forma de
  // pagamento, seleciona sozinho — o operador não devia precisar navegar um
  // autocomplete só pra confirmar a única opção que existe.
  useEffect(() => {
    if (!shouldShowConta || pagamento.idconta) return;
    let cancelado = false;
    const tipoFiltro = isPagamentoContaDestino ? 'banco' : undefined;
    ContaBancariaService.getAllList('', '', tipoFiltro, 50).then((result) => {
      if (cancelado || result instanceof Error) return;
      if (result.data.length === 1) {
        setPagamento((prev) => (prev.idconta ? prev : { ...prev, idconta: result.data[0].idconta }));
      }
    });
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagamento.tpag, shouldShowConta]);

  // Enter avança pro primeiro campo obrigatório ainda vazio da forma atual —
  // ou pro botão de emitir, se não sobrar nada pra preencher (mesmo espírito
  // de "Enter avança" de um formulário de PDV).
  const focarProximoCampoObrigatorio = () => {
    if (shouldShowConta && !pagamento.idconta) { contaInputRef.current?.focus(); return; }
    if (shouldShowBandeira && (!pagamento.tband || pagamento.tband === '00')) { bandeiraSelectRef.current?.focus(); return; }
    if (shouldShowCaut && !pagamento.caut) { cautInputRef.current?.focus(); return; }
    emitirButtonRef.current?.focus();
  };

  // Foco automático no primeiro campo obrigatório assim que a forma muda —
  // o operador não deveria precisar clicar em nada pra começar a digitar
  // (ex.: selecionar PIX Dinâmico já deixa o cursor pronto no ID da
  // transação). Só dispara quando a conta (se aplicável) já está resolvida,
  // senão focaria nela antes do useEffect de auto-seleção rodar.
  useEffect(() => {
    if (!avancarFocoAutomaticoRef.current) return;
    if (shouldShowConta && !pagamento.idconta) return;
    const timeoutId = setTimeout(focarProximoCampoObrigatorio, 60);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagamento.tpag, pagamento.idconta]);

  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = formatarCPF(e.target.value);
    setPagamento({ ...pagamento, cpf: value });
    if (cpfError) setCpfError('');

    const cpfLimpo = value.replace(/[^\d]/g, '');
    if (cpfLimpo.length === 14) {
      setCpfError('CNPJ não é permitido. Informe um CPF válido');
      return;
    }

    if (cpfLimpo.length === 11) {
      if (!validarCPF(cpfLimpo)) {
        setCpfError('CPF inválido');
      } else {
        setCpfError('');
      }
    } else if (cpfLimpo.length > 0 && cpfLimpo.length !== 11) {
      setCpfError('CPF deve conter 11 dígitos');
    } else {
      setCpfError('');
    }
  };

  const handleNomeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPagamento({ ...pagamento, nome: value });
    if (nomeError) setNomeError('');

    if (value && value.length < 3) {
      setNomeError('Nome deve ter no mínimo 3 caracteres');
    } else if (value && value.length > 100) {
      setNomeError('Nome deve ter no máximo 100 caracteres');
    } else {
      setNomeError('');
    }
  };

  const handleBandeiraChange = (value: string) => {
    setPagamento({ ...pagamento, tband: value });
    if (tbandError) setTbandError('');
    // Reage à MUDANÇA DE VALOR, não ao keydown do <Select> — com o menu
    // aberto, o Enter que confirma a bandeira é tratado pelo Menu/Popover
    // do MUI antes de chegar no onKeyDown do próprio Select, então esse
    // nunca disparava. onChange sempre roda, seja por clique ou por Enter
    // na lista. O setTimeout dá tempo do MUI fechar o menu e devolver o
    // foco pro Select antes da gente roubar de novo pro campo seguinte.
    if (value) setTimeout(() => cautInputRef.current?.focus(), 80);
  };

  const handleCautChange = (value: string) => {
    setPagamento({ ...pagamento, caut: value });
    if (cautError) setCautError('');

    if (isPagamentoCartao && !value) {
      setCautError('Código de autorização é obrigatório');
    } else if (isPagamentoPix && !value) {
      setCautError('ID da transação PIX é obrigatório');
    } else {
      setCautError('');
    }
  };

  // Devolve o primeiro campo inválido (ref + setter de erro), pra F12/Emitir
  // jogar o foco nele em vez de só recusar em silêncio.
  const validarEFocarPrimeiroErro = (): boolean => {
    let valido = true;

    if (isPagamentoCartao) {
      if (!pagamento.tband || pagamento.tband === '00') {
        setTbandError('Bandeira do cartão é obrigatória');
        valido = false;
      }
      if (!pagamento.caut) {
        setCautError('Código de autorização é obrigatório');
        valido = false;
      }
    }

    if (isPagamentoPix && !pagamento.caut) {
      setCautError('ID da transação PIX é obrigatório');
      valido = false;
    }

    if ((isPagamentoContaDestino || isPagamentoSemPagamento) && !pagamento.idconta) {
      setIdcontaError(isPagamentoSemPagamento ? 'Selecione a conta de destino' : 'Selecione a conta bancária de destino');
      valido = false;
    }

    // CPF/nome na nota são opcionais — só reprovam a emissão se o operador
    // começou a preencher algo e ficou incompleto/inválido; em branco, não
    // bloqueia nada.
    const cpfLimpo = pagamento.cpf?.replace(/[^\d]/g, '') || '';
    let cpfInvalido = false;
    if (pagamento.cpf) {
      if (cpfLimpo.length === 14) {
        setCpfError('CNPJ não é permitido. Informe um CPF válido');
        cpfInvalido = true;
      } else if (cpfLimpo.length !== 11) {
        setCpfError('CPF deve conter 11 dígitos');
        cpfInvalido = true;
      } else if (!validarCPF(cpfLimpo)) {
        setCpfError('CPF inválido');
        cpfInvalido = true;
      }
    }

    let nomeInvalido = false;
    if (pagamento.nome && pagamento.nome.length < 3) {
      setNomeError('Nome deve ter no mínimo 3 caracteres');
      nomeInvalido = true;
    } else if (pagamento.nome && pagamento.nome.length > 100) {
      setNomeError('Nome deve ter no máximo 100 caracteres');
      nomeInvalido = true;
    }

    if (cpfInvalido || nomeInvalido) valido = false;

    if (!valido) {
      // Foca o primeiro campo que ainda está com problema, na mesma ordem
      // que focarProximoCampoObrigatorio usa.
      if (shouldShowConta && !pagamento.idconta) contaInputRef.current?.focus();
      else if (shouldShowBandeira && (!pagamento.tband || pagamento.tband === '00')) bandeiraSelectRef.current?.focus();
      else if (shouldShowCaut && !pagamento.caut) cautInputRef.current?.focus();
      else if (cpfInvalido || nomeInvalido) { setOpenCpfDialog(true); setTimeout(() => cpfInputRef.current?.focus(), 80); }
    }

    return valido;
  };

  const handleConfirmarPagamento = async (dados: IPagamentoData) => {
    let progressCleanup: (() => void) | null = null;

    try {
      setIsLoading(true);
      setShowFullScreenLoading(true);
      setLoadingMessage('Validando dados da nota fiscal...');

      progressCleanup = simulateProgress();

      if (!validarEFocarPrimeiroErro()) {
        setIsLoading(false);
        setShowFullScreenLoading(false);
        return;
      }

      setLoadingMessage('Enviando para SEFAZ...');
      setLoadingProgress(40);

      const validationSchema = getValidationSchema(isPagamentoCartao, isPagamentoPix);
      const dadosValidados = await validationSchema.validate(dados, {
        abortEarly: false,
        stripUnknown: true,
      });

      setLoadingMessage('Processando autorização da nota...');
      setLoadingProgress(60);

      const dadosCompletos = {
        id: dadosValidados.id,
        idconta: dadosValidados.idconta,
        tcnpj: dadosValidados.tcnpj || '',
        faturado: false,
        indpag: dadosValidados.indpag,
        tpag: dadosValidados.tpag,
        // Promoção saiu da tela de emissão fiscal — decisão de
        // desconto/promoção já deve estar definida antes de chegar aqui
        // (venda → desconto → faturamento → emissão), não durante a NFC-e.
        promocao: false,
        tband: shouldShowBandeira ? dadosValidados.tband || '' : '',
        caut: shouldShowCaut ? dadosValidados.caut || '' : '',
        cpf: dados.cpf ? dados.cpf.replace(/[^\d]/g, '') : '',
        nome: dados.nome || '',
      };

      setLoadingMessage('Gerando NFC-e...');
      setLoadingProgress(80);

      const resultado = await FaturamentoService.createnfce(dadosCompletos);

      if (!(resultado instanceof Blob)) {
        throw new Error('Arquivo da NFC-e inválido.');
      }

      setLoadingMessage('Nota autorizada! Preparando visualização...');
      setLoadingProgress(100);

      // Pequeno delay para o usuário ver o 100%
      await new Promise(resolve => setTimeout(resolve, 500));

      // Criar URL do blob e abrir no visualizador
      const url = URL.createObjectURL(resultado);
      setPdfBlobUrl(url);
      setOpenPdfViewer(true);

      setShowFullScreenLoading(false);
      setTms('success');
      setMsg('NFC-e gerada com sucesso!');
      setOpenmsg(true);

    } catch (error: any) {
      console.error('Erro na emissão:', error);
      setShowFullScreenLoading(false);

      setTms('error');
      if (error.name === 'ValidationError') {
        setMsg(error.errors.join('\n'));
      } else {
        setMsg(error.message || 'Erro inesperado ao gerar NFC-e.');
      }
      setOpenmsg(true);
    } finally {
      setIsLoading(false);
      if (progressCleanup) {
        progressCleanup();
      }
    }
  };

  const handleCancel = () => {
    if (showFullScreenLoading) {
      return;
    }
    resetDialog();
    onClose();
  };

  const handleVerificarNota = async () => {
    if (!pagamento.id || verificandoNota || showFullScreenLoading) return;
    setVerificandoNota(true);
    const resultado = await FaturamentoService.verificarNfce(pagamento.id);
    setVerificandoNota(false);

    if (resultado instanceof Error) {
      setTms('error');
      setMsg(resultado.message);
      setOpenmsg(true);
      return;
    }
    if (resultado.valido) {
      setTms('success');
      setMsg('Nenhum problema encontrado na configuração tributária dos itens.');
    } else {
      setTms('warning');
      setMsg(
        'Configuração tributária incompleta:\n' +
        (resultado.problemas || []).map((problema) => `• ${problema}`).join('\n')
      );
    }
    setOpenmsg(true);
  };

  const additionalTitle = isPagamentoCartao ? 'Dados do Cartão' : isPagamentoPix ? 'Dados do PIX' : null;

  const isConfirmDisabled = () => {
    if (isLoading || showFullScreenLoading) return true;
    if (shouldShowConta && !pagamento.idconta) return true;
    if (isPagamentoCartao) {
      if (!pagamento.tband || pagamento.tband === '00' || !pagamento.caut) return true;
    }

    if (isPagamentoPix && !pagamento.caut) return true;

    // CPF é opcional — só bloqueia se o operador começou a preencher e ficou
    // incompleto/inválido; em branco, não impede a emissão.
    if (pagamento.cpf) {
      const cpfLimpo = pagamento.cpf.replace(/[^\d]/g, '');
      if (cpfLimpo.length !== 11 || !validarCPF(cpfLimpo) || !pagamento.nome || pagamento.nome.length < 3) {
        return true;
      }
    }

    return false;
  };

  // ── Orquestração de teclado da dialog inteira (F4/F6/F12/ESC e os
  // atalhos numéricos 1-7 das formas de pagamento) — em capture phase e com
  // stopPropagation, pra ter prioridade sobre qualquer atalho de fundo do
  // PDV/Faturamento enquanto essa dialog estiver aberta (ver Faturamento.tsx:
  // confirmado que não há listener nenhum ativo nesse nível hoje, mas o
  // cuidado fica valendo pra qualquer atalho futuro). Um listener só,
  // recriado/limpo a cada mudança de estado relevante — nunca acumula.
  useEffect(() => {
    if (!open || openPdfViewer) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      // Com a dialog de CPF na nota aberta, ESC fecha só ela (não a NFC-e
      // inteira) e nenhum outro atalho da dialog principal deve disparar
      // por trás enquanto o operador está preenchendo CPF/nome.
      if (openCpfDialog) {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          setOpenCpfDialog(false);
        }
        return;
      }

      if (event.key === 'Escape') {
        // Emissão em andamento: nunca fecha sozinho, nem acidentalmente.
        if (showFullScreenLoading) { event.preventDefault(); event.stopPropagation(); return; }
        event.preventDefault();
        event.stopPropagation();
        handleCancel();
        return;
      }

      if (isEventoDeDigitacao(event.target)) return;

      if (event.key === 'F12') {
        event.preventDefault();
        event.stopPropagation();
        if (!isConfirmDisabled()) handleConfirmarPagamento(pagamento);
        else validarEFocarPrimeiroErro();
        return;
      }
      if (event.key === 'F4') {
        if (!shouldShowConta) return;
        event.preventDefault();
        event.stopPropagation();
        contaInputRef.current?.focus();
        return;
      }
      if (event.key === 'F6') {
        event.preventDefault();
        event.stopPropagation();
        setOpenCpfDialog(true);
        setTimeout(() => cpfInputRef.current?.focus(), 80);
        return;
      }
      const numero = Number(event.key);
      if (numero >= 1 && numero <= TODAS_FORMAS_PAGAMENTO.length) {
        event.preventDefault();
        event.stopPropagation();
        avancarFocoAutomaticoRef.current = true;
        selecionarForma(TODAS_FORMAS_PAGAMENTO[numero - 1].id);
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, openPdfViewer, showFullScreenLoading, pagamento, shouldShowConta, openCpfDialog]);

  const corBg = theme.torq.sidebar.bg;
  const corAccent = theme.torq.sidebar.iconActive;
  const corBorda = theme.palette.mode === 'dark' ? theme.palette.divider : '#DCE5EA';

  return (
    <>
      {showFullScreenLoading && (
        <FullScreenLoading
          message={loadingMessage}
          progress={loadingProgress}
        />
      )}

      <Dialog
        open={open && !openPdfViewer}
        onClose={handleCancel}
        maxWidth={false}
        PaperProps={{ sx: { width: 'min(640px, 94vw)', borderRadius: '20px', overflow: 'hidden', maxHeight: '92vh' } }}
      >
        {/* HEADER — gradiente escuro com dois feixes diagonais (pseudo-elementos), sem imagem
            externa: ::before cria a faixa mais escura, ::after a faixa azul translúcida. */}
        <Box
          sx={{
            position: 'relative',
            minHeight: 118,
            px: '34px', py: '26px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            overflow: 'hidden',
            background: 'linear-gradient(110deg, #062D3A 0%, #083D4A 48%, #0B4F60 100%)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -80, left: '42%',
              width: 260, height: 250,
              background: 'rgba(0, 16, 32, 0.34)',
              transform: 'rotate(33deg)',
              pointerEvents: 'none',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              top: -120, right: '6%',
              width: 420, height: 290,
              background: 'linear-gradient(135deg, rgba(44, 107, 156, 0.18), rgba(44, 107, 156, 0.02))',
              pointerEvents: 'none',
            },
            '& > *': { position: 'relative', zIndex: 2 },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
            <Box
              sx={{
                width: 44, height: 44, borderRadius: '12px',
                bgcolor: alpha(corAccent, 0.16),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: corAccent,
              }}
            >
              <ReceiptIcon />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '24px', fontWeight: 700, lineHeight: 1.2, color: '#FFFFFF' }}>
                Emitir NFC-e
              </Typography>
              <Typography variant="caption" sx={{ color: alpha('#FFFFFF', 0.65) }}>
                Nota Fiscal de Consumidor Eletrônica
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                px: 1, py: 0.4, borderRadius: '6px',
                border: `1px solid ${alpha('#FFFFFF', 0.3)}`,
                fontSize: '0.7rem', fontWeight: 700, color: alpha('#FFFFFF', 0.85),
              }}
            >
              ESC
            </Box>
            <IconButton
              onClick={handleCancel}
              disabled={showFullScreenLoading}
              size="small"
              sx={{ color: '#FFFFFF', '&:hover': { bgcolor: alpha('#FFFFFF', 0.1) } }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* RESUMO DA VENDA */}
        <DocumentoResumoBar
          numerodocumento={numerodocumento}
          emissaodata={emissaodata}
          parceironome={parceironome}
          valortotal={valortotal}
          cpfNaNotaSlot={{
            cpf: pagamento.cpf || undefined,
            nome: pagamento.nome || undefined,
            onClick: () => { if (!showFullScreenLoading) setOpenCpfDialog(true); },
          }}
        />

        <DialogContent sx={{ p: 0 }}>
          {isLoading && !showFullScreenLoading && (
            <LinearProgress color="primary" sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2 }} />
          )}

          <Box sx={{ p: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            {/* FORMAS DE PAGAMENTO — coluna própria à esquerda, com os dados
                da forma selecionada (conta bancária/cartão) ao lado, não
                embaixo (ver PaymentMethodSelector.tsx pra navegação/scroll). */}
            <Box sx={{ width: { xs: '100%', sm: 200 }, flexShrink: 0 }}>
              <PaymentMethodSelector
                ref={paymentSelectorRef}
                formas={TODAS_FORMAS_PAGAMENTO}
                selecionadoId={pagamento.tpag}
                destacadoId={formaDestacada}
                onDestacar={setFormaDestacada}
                onConfirmar={(id, viaTeclado) => {
                  avancarFocoAutomaticoRef.current = viaTeclado;
                  selecionarForma(id);
                  if (viaTeclado) setTimeout(focarProximoCampoObrigatorio, 60);
                }}
                disabled={showFullScreenLoading}
                orientation="vertical"
              />
            </Box>

            {/* DADOS DA FORMA SELECIONADA. Quando a forma não pede nenhum
                campo extra (Dinheiro, o caso mais comum — vai direto pro
                caixa da sessão ativa), mostra o painel "pronto pra emitir"
                em vez de deixar esse espaço vazio. */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {(shouldShowConta || shouldShowBandeira || shouldShowCaut) ? (
              <Box
                sx={{
                  border: `1px solid ${corBorda}`,
                  borderRadius: '14px',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    px: 2, py: 1.25,
                    bgcolor: alpha(corAccent, 0.06),
                    borderBottom: `1px solid ${corBorda}`,
                  }}
                >
                  <Box sx={{ color: theme.palette.primary.dark, display: 'flex' }}>
                    {TODAS_FORMAS_PAGAMENTO.find((f) => f.id === pagamento.tpag)?.icon}
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                    {TODAS_FORMAS_PAGAMENTO.find((f) => f.id === pagamento.tpag)?.nome}
                  </Typography>
                </Box>

                {shouldShowConta && (
                  // Linha própria, largura total — encolhida dentro do
                  // cabeçalho (dividindo espaço com o título via
                  // space-between) o nome do banco/titular ficava cortado.
                  <Box sx={{ px: 2, pt: 2, pb: (shouldShowBandeira || shouldShowCaut) ? 0 : 2 }}>
                    <AutoCompleteContaBancariaSimple
                      label={
                        isPagamentoContaDestino
                          ? 'Conta bancária de destino'
                          : isPagamentoSemPagamento
                            ? 'Conta de destino (caixa ou banco)'
                            : 'Conta'
                      }
                      value={pagamento.idconta}
                      // PIX Estático/Transferência só aceitam banco; Sem
                      // Pagamento aceita qualquer tipo (caixa ou banco) — não
                      // passa `tipo` nenhum pro autocomplete não filtrar.
                      tipo={isPagamentoContaDestino ? 'banco' : undefined}
                      inputRef={contaInputRef}
                      onChange={(idconta) => {
                        setPagamento({ ...pagamento, idconta });
                        setIdcontaError('');
                      }}
                      isExternalLoading={isLoading}
                      disabled={showFullScreenLoading}
                      error={!!idcontaError}
                      helperText={idcontaError}
                      fullWidth
                    />
                  </Box>
                )}

                {(shouldShowBandeira || shouldShowCaut) && (
                  // Empilhados um em cima do outro, em ordem (Bandeira depois
                  // Autorização) — a dialog ficou estreita demais pra caber
                  // os dois lado a lado sem cortar o texto.
                  <Box sx={{ px: 2, py: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {shouldShowBandeira && (
                      <FormControl size="small" required fullWidth>
                        <InputLabel shrink sx={{ fontSize: '0.7rem' }}>Bandeira</InputLabel>
                        <Select
                          inputRef={bandeiraSelectRef}
                          size="small"
                          value={pagamento.tband || ''}
                          onChange={(e) => handleBandeiraChange(e.target.value)}
                          sx={{ fontSize: '0.875rem' }}
                          error={!!tbandError}
                          disabled={showFullScreenLoading}
                        >
                          <MenuItem value="">
                            <em>Selecione a bandeira</em>
                          </MenuItem>
                          {BANDEIRAS_CARTAO.map((b) => (
                            <MenuItem key={b.id} value={b.id}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: b.cor }} />
                                <Typography fontSize="0.875rem">{b.nome}</Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                        {tbandError && (
                          <Typography variant="caption" color="error.dark" sx={{ mt: 0.5, ml: 0.5 }}>
                            {tbandError}
                          </Typography>
                        )}
                      </FormControl>
                    )}

                    {shouldShowCaut && (
                      <TextField
                        inputRef={cautInputRef}
                        size="small"
                        fullWidth
                        sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                        label={isPagamentoPix ? 'ID da Transação PIX' : 'Código de Autorização'}
                        value={pagamento.caut}
                        onChange={(e) => handleCautChange(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); focarProximoCampoObrigatorio(); } }}
                        placeholder={isPagamentoPix ? 'Digite ou cole o ID da transação...' : 'Digite o código de autorização'}
                        required
                        error={!!cautError}
                        helperText={cautError}
                        disabled={showFullScreenLoading}
                      />
                    )}
                  </Box>
                )}
              </Box>
              ) : (
                <FormaProntaPanel
                  titulo={
                    isPagamentoDinheiro
                      ? 'Pagamento em dinheiro'
                      : `${TODAS_FORMAS_PAGAMENTO.find((f) => f.id === pagamento.tpag)?.nome} selecionado`
                  }
                  descricao={
                    isPagamentoDinheiro
                      ? 'Entra direto no caixa da sessão ativa de quem está emitindo — nenhuma conta ou dado extra precisa ser informado.'
                      : 'Nenhuma informação adicional é necessária para essa forma de pagamento.'
                  }
                  documentoLabel="para emitir a NFC-e"
                />
              )}
            </Box>
          </Box>
        </DialogContent>

        {/* ATALHOS + AÇÕES */}
        <DialogActions
          sx={{
            px: 3, py: 1.75,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', rowGap: 1.5,
            borderTop: `1px solid ${corBorda}`,
            bgcolor: theme.palette.mode === 'dark' ? 'background.default' : '#F8FAFC',
          }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <KeyHintRow keyLabel="↑↓">Navegar</KeyHintRow>
            <KeyHintRow keyLabel="ENTER">Selecionar</KeyHintRow>
            {shouldShowConta && <KeyHintRow keyLabel="F4">Alterar conta</KeyHintRow>}
            <KeyHintRow keyLabel="F6">CPF na nota</KeyHintRow>
            <KeyHintRow keyLabel="F12">Emitir NFC-e</KeyHintRow>
            <KeyHintRow keyLabel="ESC">Cancelar</KeyHintRow>
          </Box>
          {/* flexShrink: 0 + whiteSpace: nowrap — sem isso, quando o espaço
              fica curto (chips de atalho à esquerda competindo pela mesma
              linha), o flexbox encolhia esses botões até o texto quebrar em
              duas linhas, deformando a pílula arredondada. */}
          <Box sx={{ display: 'flex', gap: 1.25, flexShrink: 0 }}>
            <Button
              onClick={handleVerificarNota}
              variant="outlined"
              color="secondary"
              disabled={showFullScreenLoading || verificandoNota || !pagamento.id}
              startIcon={verificandoNota ? <CircularProgress size={16} /> : <FactCheckIcon fontSize="small" />}
              sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              Verificar Nota
            </Button>
            <Button
              onClick={handleCancel}
              variant="outlined"
              color="inherit"
              disabled={showFullScreenLoading}
              sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              <CloseIcon fontSize="small" sx={{ mr: 0.5 }} /> Esc Cancelar
            </Button>
            <Button
              ref={emitirButtonRef}
              onClick={() => (isConfirmDisabled() ? validarEFocarPrimeiroErro() : handleConfirmarPagamento(pagamento))}
              variant="contained"
              disabled={isLoading || showFullScreenLoading}
              startIcon={isLoading ? <CircularProgress size={16} sx={{ color: theme.torq.sidebar.bg }} /> : <ReceiptIcon fontSize="small" />}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 700,
                whiteSpace: 'nowrap',
                flexShrink: 0,
                bgcolor: corAccent,
                color: corBg,
                '&:hover': { bgcolor: alpha(corAccent, 0.85) },
                '&.Mui-disabled': { bgcolor: alpha(corAccent, 0.35), color: alpha(corBg, 0.6) },
              }}
            >
              {isLoading ? 'Emitindo NFC-e...' : 'F12  Emitir NFC-e'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* CPF na nota — opcional, aberta pelo atalho no resumo (no lugar do
          nome do cliente, já que NFC-e não tem cliente cadastrado na venda)
          ou por F6. Fica em cima da dialog principal, que continua aberta
          por trás. */}
      <Dialog
        open={openCpfDialog}
        onClose={() => setOpenCpfDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BadgeIcon sx={{ color: theme.palette.primary.dark }} />
            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>CPF na nota</Typography>
          </Box>
          <IconButton size="small" onClick={() => setOpenCpfDialog(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Opcional — informe o CPF do consumidor para identificá-lo na nota.
          </Typography>
          <TextField
            inputRef={cpfInputRef}
            autoFocus
            size="small"
            fullWidth
            label="CPF do consumidor"
            value={pagamento.cpf}
            onChange={handleCpfChange}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); document.getElementById('nfce-nome-consumidor')?.focus(); } }}
            placeholder="000.000.000-00"
            error={!!cpfError}
            helperText={cpfError}
            disabled={showFullScreenLoading}
          />
          <TextField
            id="nfce-nome-consumidor"
            size="small"
            fullWidth
            label="Nome completo"
            value={pagamento.nome}
            onChange={handleNomeChange}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setOpenCpfDialog(false); } }}
            placeholder="Digite o nome completo"
            error={!!nomeError}
            helperText={nomeError}
            disabled={showFullScreenLoading}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setOpenCpfDialog(false)}
            variant="contained"
            sx={{
              borderRadius: '10px', textTransform: 'none', fontWeight: 700,
              bgcolor: corAccent, color: corBg,
              '&:hover': { bgcolor: alpha(corAccent, 0.85) },
            }}
          >
            Concluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Visualizador de PDF da NFC-e */}
      <Dialog
        open={openPdfViewer}
        onClose={handleClosePdfViewer}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PictureAsPdfIcon color="error" />
            NFC-e - Documento Fiscal Eletrônico
          </Typography>
          <Box>
            <IconButton onClick={handleDownloadPdf} sx={{ mr: 1 }} title="Download">
              <DownloadIcon />
            </IconButton>
            <IconButton onClick={handlePrintPdf} sx={{ mr: 1 }} title="Imprimir">
              <PrintIcon />
            </IconButton>
            <IconButton onClick={handleClosePdfViewer}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, overflow: 'auto' }}>
          {isGeneratingPdf || !pdfBlobUrl ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: 400, flexDirection: 'column', gap: 2 }}>
              <LinearProgress sx={{ width: '80%' }} />
              <Typography>Gerando NFC-e...</Typography>
            </Box>
          ) : (
            <iframe
              src={pdfBlobUrl}
              style={{
                width: '100%',
                height: 'calc(90vh - 120px)',
                border: 'none',
              }}
              title="Visualizador de NFC-e"
            />
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openmsg}
        autoHideDuration={tms === 'success' ? 2000 : null}
        onClose={handleCloseMsg}
        sx={{ mt: 8 }}
      >
        <Alert
          onClose={handleCloseMsg}
          severity={tms}
          variant="filled"
          elevation={6}
          sx={{
            width: '100%',
            minWidth: '280px',
            borderRadius: 2,
            '& .MuiAlert-message': {
              flex: 1,
              py: 0.5,
              whiteSpace: 'pre-line',
            },
            '& .MuiAlert-icon': {
              fontSize: '1.2rem',
              alignItems: 'center',
            },
            ...(tms === 'error' && {
              backgroundColor: '#CF2330',
            }),
            ...(tms === 'success' && {
              backgroundColor: '#27801A',
            }),
            ...(tms === 'warning' && {
              backgroundColor: '#ed6c02',
            }),
            ...(tms === 'info' && {
              backgroundColor: '#0288d1',
            }),
          }}
          action={
            tms !== 'success' ? (
              <Button
                color="inherit"
                size="small"
                onClick={handleCloseMsg}
                sx={{
                  fontWeight: 700,
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  borderRadius: 1.5,
                  px: 1.5,
                  py: 0.5,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.3)',
                  },
                }}
              >
                FECHAR
              </Button>
            ) : null
          }
        >
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.3, color: '#FFFFFF' }}>
              {tms === 'error' ? '❌ ERRO' :
                tms === 'warning' ? '⚠️ ATENÇÃO' :
                  tms === 'success' ? '✅ SUCESSO' : 'ℹ️ INFORMAÇÃO'}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.4, color: '#FFFFFF' }}>
              {msg}
            </Typography>
          </Box>
        </Alert>
      </Snackbar>
    </>
  );
};
